import {
  asStableCallback,
  useStableCallback,
} from "@bezoned/shared/hooks/use-stable-callback.js";
import type { TeamAvailableChannelsGetResponse } from "@bezoned/shared/types/api/team/team-available-channels-get.types.js";
import type {
  TeamOfficeGetResponse,
  TeamOfficeTable,
} from "@bezoned/shared/types/api/team/team-office-get.types.js";
import type { TeamOfficeUpdateBody } from "@bezoned/shared/types/api/team/team-office-update.types.js";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from "@fluentui/react-components";
import { Info20Regular } from "@fluentui/react-icons";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { teamOfficeUpdate } from "../../../../../services/api.service.js";
import { recordErrorInSentry } from "../../../../../utils/init-sentry.js";
import {
  CHANNEL_SPECIAL_OPTIONS,
  getChannelAssignmentFromValue,
  getChannelSelectedOptions,
  getChannelValueLabel,
  getTableChannelAssignmentPayload,
} from "./channel-assignment.helpers.js";
import type {
  ChannelDraftAssignment,
  ChannelSelectorValue,
} from "./channel-assignment.helpers.js";

type PresetTable = NonNullable<
  TeamOfficeGetResponse["preset"]["manifest"]["tables"]
>[number];

type AvailableChannel = TeamAvailableChannelsGetResponse["channels"][number];

type DraftTableAssignment = ChannelDraftAssignment;

interface TeamEditThemeConfigDialogProps {
  open: boolean;
  onClose: () => void;
  msTeamId: string;
  officeTables: TeamOfficeTable[];
  presetTables: PresetTable[];
  availableChannels: AvailableChannel[];
  isLoading: boolean;
  onOfficeUpdateSuccess: () => void;
}

const getEffectiveAssignment = (
  presetTableId: string,
  officeTableByPresetId: Map<string, TeamOfficeTable>,
  assignments: Record<string, DraftTableAssignment>
): DraftTableAssignment => {
  const override = assignments[presetTableId];
  if (override) {
    return override;
  }

  const existingMsChannelId =
    officeTableByPresetId.get(presetTableId)?.msChannelId;
  if (existingMsChannelId) {
    return { mode: "existing_channel", msChannelId: existingMsChannelId };
  }

  return { mode: "not_used" };
};

const buildEffectiveAssignmentsByPresetId = ({
  presetTables,
  officeTableByPresetId,
  assignments,
}: {
  presetTables: PresetTable[];
  officeTableByPresetId: Map<string, TeamOfficeTable>;
  assignments: Record<string, DraftTableAssignment>;
}): Record<string, DraftTableAssignment> => {
  const next: Record<string, DraftTableAssignment> = {};

  for (const presetTable of presetTables) {
    next[presetTable.id] = getEffectiveAssignment(
      presetTable.id,
      officeTableByPresetId,
      assignments
    );
  }

  return next;
};

const getTableDisplayName = (presetTable: PresetTable): string =>
  presetTable.defaultName ?? "Unknown table";

const getDuplicateAssignedChannelNames = ({
  effectiveAssignmentsByPresetId,
  channelNameById,
}: {
  effectiveAssignmentsByPresetId: Record<string, DraftTableAssignment>;
  channelNameById: Map<string, string>;
}): string[] => {
  const channelUsageCount = new Map<string, number>();

  for (const assignment of Object.values(effectiveAssignmentsByPresetId)) {
    if (assignment.mode !== "existing_channel" || !assignment.msChannelId) {
      continue;
    }

    const currentCount = channelUsageCount.get(assignment.msChannelId) ?? 0;
    channelUsageCount.set(assignment.msChannelId, currentCount + 1);
  }

  return [...channelUsageCount.entries()]
    .filter(([, count]) => count > 1)
    .map(([channelId]) => channelNameById.get(channelId) ?? channelId);
};

export const TeamEditThemeConfigDialog = ({
  open,
  onClose,
  msTeamId,
  officeTables,
  presetTables,
  availableChannels,
  isLoading,
  onOfficeUpdateSuccess,
}: TeamEditThemeConfigDialogProps) => {
  const [assignments, setAssignments] = useState<
    Record<string, DraftTableAssignment>
  >({});
  const [validationError, setValidationError] = useState<string | null>(null);

  const officeTableByPresetId = useMemo(
    () =>
      new Map(
        officeTables.map((officeTable) => [
          officeTable.presetTableId,
          officeTable,
        ])
      ),
    [officeTables]
  );

  const availableChannelNameById = useMemo(
    () =>
      new Map(
        availableChannels.map((channel) => [channel.msChannelId, channel.name])
      ),
    [availableChannels]
  );

  const effectiveAssignmentsByPresetId = useMemo(
    () =>
      buildEffectiveAssignmentsByPresetId({
        assignments,
        officeTableByPresetId,
        presetTables,
      }),
    [presetTables, officeTableByPresetId, assignments]
  );

  const tableRows = useMemo(
    () =>
      presetTables.map((presetTable) => {
        const effectiveAssignment = effectiveAssignmentsByPresetId[
          presetTable.id
        ] ?? { mode: "not_used" as const };

        return {
          displayName: getTableDisplayName(presetTable),
          effectiveAssignment,
          key: presetTable.id,
        };
      }),
    [presetTables, effectiveAssignmentsByPresetId]
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: TeamOfficeUpdateBody = {
        assignments: presetTables.map((presetTable) => {
          const effective = effectiveAssignmentsByPresetId[presetTable.id] ?? {
            mode: "not_used" as const,
          };

          return getTableChannelAssignmentPayload(presetTable.id, effective);
        }),
      };

      return teamOfficeUpdate({ msTeamId, ...body });
    },
    onError: (error) => {
      recordErrorInSentry(
        error instanceof Error ? error : new Error(String(error))
      );
    },
    onSuccess: () => {
      setAssignments({});
      setValidationError(null);
      onOfficeUpdateSuccess();
    },
  });

  const handleChannelSelection = (
    presetTableId: string,
    value: ChannelSelectorValue
  ) => {
    setValidationError(null);
    setAssignments((prev) => ({
      ...prev,
      [presetTableId]: getChannelAssignmentFromValue(value),
    }));
  };

  const handleSave = () => {
    const duplicateChannelNames = getDuplicateAssignedChannelNames({
      channelNameById: availableChannelNameById,
      effectiveAssignmentsByPresetId,
    });

    if (duplicateChannelNames.length > 0) {
      setValidationError(
        `Each channel can only be used once. Duplicate channels: ${duplicateChannelNames.join(", ")}.`
      );
      return;
    }

    setValidationError(null);
    saveMutation.mutate();
  };

  const handleClose = () => {
    setAssignments({});
    setValidationError(null);
    onClose();
  };
  const handleOpenChange249 = useStableCallback((_event, data) => {
    if (!data.open) {
      handleClose();
    }
  });

  return (
    <Dialog onOpenChange={handleOpenChange249} open={open}>
      <DialogSurface className="sm:min-w-[800px]">
        <DialogBody>
          <DialogTitle>Configure office design</DialogTitle>
          <DialogContent className="overflow-x-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner label="Loading..." />
              </div>
            ) : (
              <Table
                aria-label="Office tables configuration"
                className="w-full table-fixed"
              >
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className="w-[45%]">Table</TableHeaderCell>
                    <TableHeaderCell className="w-[55%]">
                      <div className="flex items-center gap-1">
                        <span>Channel</span>
                        <Tooltip
                          content="Each office table is connected with MS Teams Channel"
                          relationship="label"
                          withArrow
                        >
                          <Info20Regular aria-label="Channel mapping info" />
                        </Tooltip>
                      </div>
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="w-[45%] overflow-hidden text-ellipsis whitespace-nowrap">
                        {row.displayName}
                      </TableCell>
                      <TableCell className="w-[55%]">
                        <Dropdown
                          button={{ className: "w-full !min-w-0" }}
                          className="w-full !min-w-0"
                          onOptionSelect={asStableCallback((_event, data) => {
                            if (data.optionValue) {
                              handleChannelSelection(row.key, data.optionValue);
                            }
                          })}
                          selectedOptions={getChannelSelectedOptions(
                            row.effectiveAssignment
                          )}
                          value={getChannelValueLabel(
                            row.effectiveAssignment,
                            availableChannelNameById
                          )}
                        >
                          <Option value={CHANNEL_SPECIAL_OPTIONS.notUsed}>
                            Do not use
                          </Option>
                          {availableChannels.map((channel) => (
                            <Option
                              key={channel.msChannelId}
                              value={channel.msChannelId}
                            >
                              {channel.name}
                            </Option>
                          ))}
                        </Dropdown>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {Boolean(validationError) && (
              <p className="mt-3 text-sm text-red-600">{validationError}</p>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={saveMutation.isPending}
              onClick={handleClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={saveMutation.isPending || presetTables.length === 0}
              onClick={handleSave}
              type="button"
            >
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
